import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import {
  frameThickness,
  glassHingeWidth,
  glassHingeHeight,
  glassLockWidth,
  glassLockHeight,
} from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

/** Recalculate positions of panels based on frame positions */
function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const topFrame = getParam(params, 'top_frame_profile')
  const leftPanel = getParam(params, 'left_glass_panel')
  const topDoorPanel = getParam(params, 'top_door_glass_panel')
  const doorPanel1 = getParam(params, 'door_glass_panel_1')
  const doorPanel2 = getParam(params, 'door_glass_panel_2')
  const rightPanel = getParam(params, 'right_glass_panel')

  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', {
      position: { ...leftPanel.position, x: leftFrame.width, y: frameThickness },
    })
    currentX += leftPanel.width
  }

  if (topDoorPanel) {
    updateParam(model, 'top_door_glass_panel', {
      position: { ...topDoorPanel.position, x: currentX, y: frameThickness },
    })
  }

  if (doorPanel1 && topDoorPanel) {
    const transomHeight = topFrame.height + topDoorPanel.height
    updateParam(model, 'door_glass_panel_1', {
      position: { x: currentX, y: transomHeight, z: doorPanel1.position?.z || 0 },
    })
  }

  if (doorPanel2 && doorPanel1) {
    const door2X = currentX + doorPanel1.width
    const transomHeight = topFrame.height + (topDoorPanel?.height || 0)
    updateParam(model, 'door_glass_panel_2', {
      position: { x: door2X, y: transomHeight, z: doorPanel2.position?.z || 0 },
    })
    currentX = door2X + doorPanel2.width
  }

  if (rightPanel) {
    updateParam(model, 'right_glass_panel', {
      position: { ...rightPanel.position, x: currentX, y: frameThickness },
    })
  }
}

function updateDoorHardware(model: ModelData, panelId: string, doorHeight: number) {
  const panel = getParam(model.parameters!, panelId)
  if (!panel?.hardware) return

  const updatedHardware = { ...panel.hardware }

  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => ({
      ...handle,
      position: { ...handle.position, y: Math.round(doorHeight * 0.47) },
    }))
  }

  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.y < 0) {
        return { ...hinge, position: { ...hinge.position, y: -glassHingeHeight / 2 } }
      }
      return { ...hinge, position: { ...hinge.position, y: doorHeight - glassHingeHeight } }
    })
  }

  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => ({
      ...lock,
      position: { ...lock.position, y: doorHeight - glassLockHeight },
    }))
  }

  updateParam(model, panelId, { hardware: updatedHardware })
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const currentTotalWidth = model.width as number
  const widthDifference = numValue - currentTotalWidth
  const halfDifference = widthDifference / 2

  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (cascadeUpdates) {
    const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
    const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
    const leftFrame = getParam(model.parameters!, 'left_frame_profile')
    const rightFrame = getParam(model.parameters!, 'right_frame_profile')
    const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
    const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
    const leftPanel = getParam(model.parameters!, 'left_glass_panel')
    const rightPanel = getParam(model.parameters!, 'right_glass_panel')

    const totalDoorWidth = (doorPanel1?.width || 0) + (doorPanel2?.width || 0) || 1620

    if (bottomLeft && leftFrame) {
      const newLeftSectionWidth = bottomLeft.width + halfDifference
      updateParam(model, 'bottom_left_frame_profile', { width: newLeftSectionWidth })
      if (leftPanel) {
        updateParam(model, 'left_glass_panel', { width: newLeftSectionWidth - leftFrame.width })
      }
    }

    if (bottomRight && rightFrame) {
      const newRightSectionWidth = bottomRight.width + halfDifference
      updateParam(model, 'bottom_right_frame_profile', { width: newRightSectionWidth })
      if (rightPanel) {
        updateParam(model, 'right_glass_panel', { width: newRightSectionWidth - rightFrame.width })
      }
    }

    if (rightFrame && bottomLeft) {
      const newLeftSectionWidth = bottomLeft.width + halfDifference
      const newRightFrameX = newLeftSectionWidth + totalDoorWidth
      updateParam(model, 'right_frame_profile', {
        position: { ...rightFrame.position, x: newRightFrameX },
      })
      if (bottomRight) {
        updateParam(model, 'bottom_right_frame_profile', {
          position: { ...bottomRight.position, x: newRightFrameX },
        })
      }
    }

    recalculatePositions(model)
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  if (cascadeUpdates) {
    const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
    const topFrame = getParam(model.parameters!, 'top_frame_profile')
    const leftFrame = getParam(model.parameters!, 'left_frame_profile')
    const rightFrame = getParam(model.parameters!, 'right_frame_profile')

    if (topDoorPanel && topFrame) {
      const remainingDoorHeight = numValue - topDoorPanel.height - topFrame.height

      if (leftFrame) {
        updateParam(model, 'left_glass_panel', {
          height: leftFrame.height - frameThickness - frameThickness,
        })
      }
      if (rightFrame) {
        updateParam(model, 'right_glass_panel', {
          height: rightFrame.height - frameThickness - frameThickness,
        })
      }

      updateParam(model, 'door_glass_panel_1', { height: remainingDoorHeight })
      updateParam(model, 'door_glass_panel_2', { height: remainingDoorHeight })

      updateDoorHardware(model, 'door_glass_panel_1', remainingDoorHeight)
      updateDoorHardware(model, 'door_glass_panel_2', remainingDoorHeight)

      const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
      const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
      if (bottomLeft) {
        updateParam(model, 'bottom_left_frame_profile', {
          position: { ...bottomLeft.position, y: numValue - frameThickness },
        })
      }
      if (bottomRight) {
        updateParam(model, 'bottom_right_frame_profile', {
          position: { ...bottomRight.position, y: numValue - frameThickness },
        })
      }
    }
  }

  return model
}

const topCenterHeight: DimensionHandler = (model, _initial, numValue) => {
  const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!topDoorPanel || !doorPanel1 || !doorPanel2 || !topFrame) return model

  const newTopDoorHeight = numValue - topFrame.height
  const newTransomHeight = numValue

  updateParam(model, 'top_door_glass_panel', {
    height: newTopDoorHeight,
    position: { ...topDoorPanel.position, y: frameThickness },
  })

  updateParam(model, 'door_glass_panel_1', {
    position: { ...doorPanel1.position, y: newTransomHeight },
  })
  updateParam(model, 'door_glass_panel_2', {
    position: { ...doorPanel2.position, y: newTransomHeight },
  })

  const newTotalHeight = newTransomHeight + doorPanel1.height
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) {
    updateParam(model, 'left_glass_panel', {
      height: leftFrame.height - frameThickness - frameThickness,
    })
  }
  if (rightFrame) {
    updateParam(model, 'right_glass_panel', {
      height: rightFrame.height - frameThickness - frameThickness,
    })
  }

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: newTotalHeight - frameThickness },
    })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, y: newTotalHeight - frameThickness },
    })
  }

  return model
}

const centerPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!doorPanel1 || !doorPanel2 || !topDoorPanel || !topFrame) return model

  updateParam(model, 'door_glass_panel_1', { height: numValue })
  updateParam(model, 'door_glass_panel_2', { height: numValue })

  const newTotalHeight = topFrame.height + topDoorPanel.height + numValue
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) {
    updateParam(model, 'left_glass_panel', {
      height: leftFrame.height - frameThickness - frameThickness,
    })
  }
  if (rightFrame) {
    updateParam(model, 'right_glass_panel', {
      height: rightFrame.height - frameThickness - frameThickness,
    })
  }

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) {
    updateParam(model, 'bottom_left_frame_profile', {
      position: { ...bottomLeft.position, y: newTotalHeight - frameThickness },
    })
  }
  if (bottomRight) {
    updateParam(model, 'bottom_right_frame_profile', {
      position: { ...bottomRight.position, y: newTotalHeight - frameThickness },
    })
  }

  updateDoorHardware(model, 'door_glass_panel_1', numValue)
  updateDoorHardware(model, 'door_glass_panel_2', numValue)

  return model
}

const parameterWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId, cascadeUpdates = true } = options as { positionId?: string; cascadeUpdates?: boolean }
  if (!positionId) return model

  const currentParameter = getParam(model.parameters!, positionId as string)
  if (!currentParameter) return model

  const currentWidth = currentParameter.width
  const widthDifference = numValue - currentWidth
  updateParam(model, positionId as string, { width: numValue })

  if (cascadeUpdates) {
    if (positionId === 'door_glass_panel_1') {
      const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
      const newTotalDoorWidth = numValue + (doorPanel2?.width || 0)

      model.doorConfiguration = {
        ...model.doorConfiguration!,
        totalDoorWidth: newTotalDoorWidth,
        individualDoorWidth: [numValue, doorPanel2?.width || 0],
      }

      updateParam(model, 'top_door_glass_panel', { width: newTotalDoorWidth })
      model.width = (model.width as number) + widthDifference
      updateParam(model, 'top_frame_profile', { width: model.width as number })

      const updatedDoorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
      if (updatedDoorPanel1?.hardware) {
        const updatedHardware = { ...updatedDoorPanel1.hardware }
        if (updatedHardware.handles) {
          updatedHardware.handles = updatedHardware.handles.map((handle) => {
            const ratio = handle.position.x / currentWidth
            return { ...handle, position: { ...handle.position, x: numValue * ratio } }
          })
        }
        if (updatedHardware.locks) {
          updatedHardware.locks = updatedHardware.locks.map((lock) => ({
            ...lock,
            position: { ...lock.position, x: numValue - glassLockWidth * 1.5 },
          }))
        }
        updateParam(model, 'door_glass_panel_1', { hardware: updatedHardware })
      }
    } else if (positionId === 'door_glass_panel_2') {
      const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
      const newTotalDoorWidth = (doorPanel1?.width || 0) + numValue

      model.doorConfiguration = {
        ...model.doorConfiguration!,
        totalDoorWidth: newTotalDoorWidth,
        individualDoorWidth: [doorPanel1?.width || 0, numValue],
      }

      updateParam(model, 'top_door_glass_panel', { width: newTotalDoorWidth })
      model.width = (model.width as number) + widthDifference
      updateParam(model, 'top_frame_profile', { width: model.width as number })

      const updatedDoorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
      if (updatedDoorPanel2?.hardware) {
        const updatedHardware = { ...updatedDoorPanel2.hardware }
        if (updatedHardware.hinges) {
          updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
            if (hinge.positionId === 'door_2_right_top_hinge_1') {
              return { ...hinge, position: { ...hinge.position, x: numValue - glassHingeWidth / 2 } }
            }
            if (hinge.positionId === 'door_2_right_bottom_hinge_2') {
              return { ...hinge, position: { ...hinge.position, x: numValue - glassHingeWidth } }
            }
            return hinge
          })
        }
        if (updatedHardware.handles) {
          updatedHardware.handles = updatedHardware.handles.map((handle) => {
            const ratio = handle.position.x / currentWidth
            return { ...handle, position: { ...handle.position, x: numValue * ratio } }
          })
        }
        if (updatedHardware.locks) {
          updatedHardware.locks = updatedHardware.locks.map((lock) => ({
            ...lock,
            position: { ...lock.position, x: glassLockWidth },
          }))
        }
        updateParam(model, 'door_glass_panel_2', { hardware: updatedHardware })
      }
    }

    recalculatePositions(model)
  }

  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')

  if (!bottomLeft || !leftFrame) return model

  const widthDifference = numValue - bottomLeft.width
  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  updateParam(model, 'left_glass_panel', { width: numValue - leftFrame.width })

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  recalculatePositions(model)
  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')

  if (!bottomRight || !rightFrame) return model

  const widthDifference = numValue - bottomRight.width
  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  updateParam(model, 'right_glass_panel', { width: numValue - rightFrame.width })

  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const leftSectionWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 390
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const totalDoorWidth = (doorPanel1?.width || 810) + (doorPanel2?.width || 810)
  const newRightFrameX = leftSectionWidth + totalDoorWidth
  updateParam(model, 'right_frame_profile', {
    position: { ...rightFrame.position, x: newRightFrameX },
  })
  updateParam(model, 'bottom_right_frame_profile', {
    position: { ...bottomRight.position, x: newRightFrameX },
  })

  recalculatePositions(model)
  return model
}

export const model2DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  topCenterHeight,
  centerPanelHeight,
  parameterWidth,
  leftFrameWidth,
  rightFrameWidth,
}
